import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

import { AppContextStore } from 'stores/app-context';
import { MetadataStore } from 'stores/metadata';
import { logoutAction, UserStore } from 'stores/user';
import { openPublicFile } from 'utils/open-public-file';
import { LocalStorageKey } from 'utils/localstorage';
import { VERSION } from 'version';
import IssueLogger from './issue-logger';
import OtherOptions from './other-options';
import { Modal, Button, RadioSelection } from 'ui/atoms';

import { ReactComponent as IconSignOut } from './icons/icon-sign-out.svg';
import { ReactComponent as IconTOU } from './icons/icon-tou.svg';
import { ReactComponent as IconPrivacyPolicy } from './icons/icon-privacy-policy.svg';
import { ReactComponent as IconVersionNumber } from './icons/icon-version-number.svg';

import './settings.css';
import GuardrailSettingsSection from './guardrail-settings';

export type Theme = 'dark' | 'light' | 'system';

const availableThemes: { label: string; value: Theme }[] = [
  {
    label: 'Light mode',
    value: 'light',
  },
  {
    label: 'Dark mode',
    value: 'dark',
  },
  {
    label: 'System',
    value: 'system',
  },
];

export function returnThemeIfSystem(theme: any) {
  if (theme === 'system') {
    return handleSystemTheme();
  }

  return theme;
}

export function handleSystemTheme(): Theme {
  if (!window.matchMedia) {
    return 'dark';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function SettingsMenuModal({
  toggleSettingsMenu,
  setToggleSettingsMenu,
  setTheme,
}) {
  const [currentMenuTheme, setCurrentMenuTheme] = useState<Theme>('light');
  const account = UserStore.useState((s) => s.userInfo?.email);
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const menuThemeFromLS = localStorage.getItem(
      LocalStorageKey.theme,
    ) as Theme;

    if (menuThemeFromLS) {
      return setCurrentMenuTheme(menuThemeFromLS);
    }

    setCurrentMenuTheme('light');
    setTheme('light');
  }, [setToggleSettingsMenu, setTheme]);

  async function logout() {
    logoutAction();

    const currentAccount = accounts[0];

    await instance.logoutRedirect({
      account: currentAccount,
    });
  }

  function updateCurrentTheme(event: React.FormEvent<HTMLInputElement>) {
    const selectedTheme = event.currentTarget.value;
    const themeToApply = returnThemeIfSystem(selectedTheme);

    setCurrentMenuTheme(selectedTheme as Theme);
    setTheme(themeToApply);

    localStorage.setItem(LocalStorageKey.theme, selectedTheme);
  }

  return (
    <Modal
      title="Settings"
      className="settings-modal"
      open={toggleSettingsMenu}
      onCancel={() => {
        setToggleSettingsMenu(false);
      }}
      footer={() => {}}
    >
      <div className="settings-modal-wrapper">
        <section className="settings-section">
          <h3>Account</h3>
          <span className="account-name">{account}</span>
        </section>

        <section className="settings-section">
          <h3>Color scheme</h3>
          <div className="settings-toggle-theme">
            {availableThemes.map(({ value, label }) => {
              return (
                <RadioSelection
                  key={value}
                  label={label}
                  name="themeSelection"
                  value={value}
                  checked={currentMenuTheme === value}
                  onChange={updateCurrentTheme}
                  cssClasses="single-line"
                />
              );
            })}
          </div>
        </section>

        <section className="settings-section">
          <h3>Guardrails</h3>
          <GuardrailSettingsSection />
        </section>

        <section className="settings-section -disabled">
          <h3>Remaining limits this month</h3>
          <UsageStatisticsSection />
        </section>

        <section className="settings-section -disabled">
          <h3>About</h3>
          <AboutSection />
        </section>

        <IssueLogger />

        <OtherOptions logout={logout} />

        <section className="settings-sign-out -disabled">
          <Button
            type="text"
            className="sign-out-button"
            icon={<IconSignOut />}
            onClick={logout}
          >
            Sign out
          </Button>
        </section>
      </div>
    </Modal>
  );
}

function AboutSection() {
  const apiVersion = AppContextStore.useState((s) => s.apiVersion);
  return (
    <div className="button-list">
      <Button
        type="text"
        icon={<IconTOU />}
        onClick={() => openPublicFile('goldie-terms-of-use.pdf')}
      >
        Terms of use
      </Button>

      <Button
        type="text"
        icon={<IconPrivacyPolicy />}
        onClick={() => openPublicFile('goldie-privacy-notice.pdf')}
      >
        Privacy notice
      </Button>

      <div className="button-list-row">
        <Button type="text" icon={<IconVersionNumber />}>
          App version
        </Button>
        <span className="setting-label version">{VERSION}</span>
      </div>

      <div className="button-list-row">
        <Button type="text" icon={<IconVersionNumber />}>
          Api version
        </Button>
        <span className="setting-label version">{apiVersion}</span>
      </div>
    </div>
  );
}

function UsageStatisticsSection() {
  const maxMonthlyPrompts = UserStore.useState((s) => s.maxMonthlyPrompts);
  const currentMonthlyUserPromptsPerModel = UserStore.useState(
    (s) => s.currentMonthlyUserPromptsPerModel,
  );
  const documentsUploaded = UserStore.useState((s) => s.documentsUploaded);
  const documentLimit = UserStore.useState((s) => s.documentLimit);
  const gptModels = MetadataStore.useState((s) => s.gptModels);

  if (!gptModels.length) {
    return <></>;
  }

  const availableModels = gptModels.filter((m) => !m.disabled && !m.psuedo);

  const getCurrentPromptsByModel = ({ id }) => {
    const current = currentMonthlyUserPromptsPerModel?.[id] || 0;
    const max = maxMonthlyPrompts?.[id]; // Add null safety check

    if (!max) {
      return `unlimited`;
    }

    return `${current}/${max}`;
  };

  return (
    <div
      data-testid="usage-statistics"
      className="button-list model-list usage-statistics"
    >
      {availableModels.map((model) => {
        return (
          <div
            data-testid="usage-stat"
            key={model.id}
            className="button-list-row"
          >
            <span>{model.displayName} prompts</span>
            <span className="setting-label">
              {getCurrentPromptsByModel(model)}
            </span>
          </div>
        );
      })}
      <div
        data-testid="usage-stat"
        key={crypto.randomUUID()}
        className="button-list-row"
      >
        <span>File uploads</span>
        <span className="setting-label">
          {documentsUploaded}/{documentLimit}
        </span>
      </div>
    </div>
  );
}
